/**
 * Password validation utilities
 * Implements comprehensive password strength checking per OWASP guidelines
 */

export interface PasswordRequirement {
  id: string
  label: string
  /** i18n interpolation params for the label (e.g. { count: minLength }). */
  labelParams?: Record<string, string | number>
  test: (password: string) => boolean
  met?: boolean
}

export interface PasswordValidationResult {
  isValid: boolean
  strength: 'weak' | 'fair' | 'good' | 'strong'
  score: number
  requirements: PasswordRequirement[]
  errors: string[]
}

/**
 * Subset of the admin-configurable backend PasswordPolicy
 * (user-service/src/types/passwordPolicy.ts) that drives the client checklist.
 */
export interface PasswordPolicyShape {
  minLength: number
  requireUppercase: boolean
  requireLowercase: boolean
  requireNumbers: boolean
  requireSpecialChars: boolean
  /** Always-on server rule: password must not contain the user's email/name. */
  preventUserInfo?: boolean
  /** Always-on server rule: password must not be a common password. */
  preventCommonPasswords?: boolean
}

/**
 * Logged-in user's identity used to enforce the `preventUserInfo` rule
 * client-side (mirrors the backend containsUserData check). The forced-change
 * user always has these from the auth context.
 */
export interface PasswordUserInfo {
  email?: string
  firstName?: string
  lastName?: string
}

/**
 * A tiny inline subset of the server's common-password list. We deliberately do
 * NOT ship the full list to the client — the rule is authoritatively enforced
 * server-side. This only catches the most obvious offenders live so the
 * checklist isn't misleadingly green; anything else the server reason surfaces.
 */
const COMMON_PASSWORD_SAMPLE = new Set([
  'password', 'password1', 'password123', '12345678', '123456789', '1234567890',
  'qwerty', 'qwerty123', 'qwertyuiop', '111111', '000000', 'iloveyou',
  'admin', 'admin123', 'welcome', 'welcome123', 'letmein', 'abc123', 'monkey',
  'dragon', 'sunshine', 'princess', 'football', 'superman', 'baseball',
])

/**
 * Returns true when the password contains the user's email local-part, first
 * name, or last name (case-insensitive, min 3 chars) — mirrors the backend
 * containsUserData() so the FE checklist matches what the server enforces.
 */
const passwordContainsUserInfo = (password: string, userInfo?: PasswordUserInfo): boolean => {
  if (!userInfo) return false
  const pw = password.toLowerCase()
  const parts: string[] = []
  if (userInfo.email) parts.push(userInfo.email.split('@')[0])
  if (userInfo.firstName) parts.push(userInfo.firstName)
  if (userInfo.lastName) parts.push(userInfo.lastName)
  return parts.some(part => {
    const p = part.toLowerCase().trim()
    return p.length >= 3 && pw.includes(p)
  })
}

/**
 * The special characters the server accepts: user-service
 * PasswordValidationService.SPECIAL_CHARS, which its error message also lists.
 */
export const PASSWORD_SPECIAL_CHARS = '@$!%*?&#^()_+-=[]{}|:;<>,./'

/**
 * Fallback used before the live policy loads (and if the policy endpoint
 * fails). Mirrors the backend DEFAULT_PASSWORD_POLICY (#585): minimum 8
 * characters, no character-class requirements.
 */
export const DEFAULT_FRONTEND_POLICY: PasswordPolicyShape = {
  minLength: 8,
  requireUppercase: false,
  requireLowercase: false,
  requireNumbers: false,
  requireSpecialChars: false,
  preventUserInfo: true,
  preventCommonPasswords: true,
}

/**
 * (#623) Build the live requirement checklist from the admin-configured policy.
 * Only enabled rules appear, and the min-length label carries the real count so
 * the UI can never claim "12 characters" while enforcing 8. Each rule id maps to
 * an i18n key under `auth:completeAccount.req.*`.
 */
export const buildPasswordRequirements = (
  policy: PasswordPolicyShape,
  userInfo?: PasswordUserInfo,
): PasswordRequirement[] => {
  const reqs: PasswordRequirement[] = [
    {
      id: 'minLength',
      label: `At least ${policy.minLength} characters`,
      labelParams: { count: policy.minLength },
      test: (password: string) => password.length >= policy.minLength,
    },
  ]
  if (policy.requireUppercase) {
    reqs.push({ id: 'uppercase', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) })
  }
  if (policy.requireLowercase) {
    reqs.push({ id: 'lowercase', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) })
  }
  if (policy.requireNumbers) {
    reqs.push({ id: 'number', label: 'One number', test: (p) => /\d/.test(p) })
  }
  if (policy.requireSpecialChars) {
    reqs.push({
      id: 'special',
      // #2208: name the full set, the same list the server's error shows.
      label: `One special character (${PASSWORD_SPECIAL_CHARS})`,
      // #684: mirror the backend's exact accepted set (PASSWORD_SPECIAL_CHARS).
      // A bare /[^A-Za-z0-9]/ let chars the server rejects (space, ~, quotes)
      // turn the checklist green, then the change failed on submit.
      test: (p) => /[@$!%*?&#^()_+=[\]{}|:;<>,.\/-]/.test(p),
    })
  }
  // Always-on server rules (#623) — surfaced so the checklist matches what the
  // backend actually enforces (otherwise it goes green then the change fails).
  if (policy.preventUserInfo) {
    reqs.push({
      id: 'preventUserInfo',
      label: 'Must not contain your name or email',
      // Live client-side check against the logged-in user's identity, mirroring
      // the backend containsUserData(). Empty password passes (nothing to test).
      test: (p) => p.length === 0 || !passwordContainsUserInfo(p, userInfo),
    })
  }
  if (policy.preventCommonPasswords) {
    reqs.push({
      id: 'preventCommonPasswords',
      // Informational + authoritatively enforced server-side. We only catch the
      // most obvious offenders here (no full list shipped to the client); if the
      // server still rejects, its specific reason is surfaced on submit.
      label: 'Must not be a common password',
      test: (p) => !COMMON_PASSWORD_SAMPLE.has(p.toLowerCase()),
    })
  }
  return reqs
}

/**
 * Default checklist (8-char, no classes) used as a fallback before the live
 * admin policy resolves. Components should prefer the `usePasswordPolicy` hook.
 */
export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = buildPasswordRequirements(DEFAULT_FRONTEND_POLICY)

/**
 * Calculate password strength based on multiple factors
 */
export const calculatePasswordStrength = (password: string): number => {
  if (!password.length) {
    return 0
  }

  let score = 0

  // Length scoring (0-30 points)
  if (password.length >= 12) score += 10
  if (password.length >= 16) score += 10
  if (password.length >= 20) score += 10

  // Character diversity (0-40 points)
  if (/[a-z]/.test(password)) score += 10
  if (/[A-Z]/.test(password)) score += 10
  if (/\d/.test(password)) score += 10
  if (/[@$!%*?&#^()_+\-=[\]{}|:;<>,./?]/.test(password)) score += 10

  // Pattern complexity (0-30 points)
  // No sequential characters
  if (!/abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789/i.test(password)) {
    score += 10
  }

  // No repeated characters (3+ times)
  if (!/(.)\1{2,}/.test(password)) {
    score += 10
  }

  // Mixed case throughout
  if (/[a-z].*[A-Z]|[A-Z].*[a-z]/.test(password) && /\d.*[a-z]|[a-z].*\d/.test(password)) {
    score += 10
  }

  if (password.length < 12) {
    return Math.min(30, score)
  }

  return Math.min(100, score)
}

/**
 * Get password strength category
 */
export const getPasswordStrength = (score: number): 'weak' | 'fair' | 'good' | 'strong' => {
  if (score < 40) return 'weak'
  if (score < 60) return 'fair'
  if (score < 80) return 'good'
  return 'strong'
}

/**
 * Get color for password strength
 */
export const getStrengthColor = (strength: 'weak' | 'fair' | 'good' | 'strong'): string => {
  switch (strength) {
    case 'weak':
      return 'bg-error-500'
    case 'fair':
      return 'bg-secondary-400'
    case 'good':
      return 'bg-accent-400'
    case 'strong':
      return 'bg-success-500'
  }
}

/**
 * Get text color for password strength
 */
export const getStrengthTextColor = (strength: 'weak' | 'fair' | 'good' | 'strong'): string => {
  switch (strength) {
    case 'weak':
      return 'text-error-700'
    case 'fair':
      return 'text-secondary-700'
    case 'good':
      return 'text-accent-700'
    case 'strong':
      return 'text-success-700'
  }
}

/**
 * Validate password against all requirements
 */
export const validatePassword = (
  password: string,
  currentPassword?: string,
  requirementList: PasswordRequirement[] = PASSWORD_REQUIREMENTS,
): PasswordValidationResult => {
  const errors: string[] = []
  const requirements = requirementList.map(req => ({
    ...req,
    met: req.test(password),
  }))

  const allRequirementsMet = requirements.every(req => req.met)
  const score = calculatePasswordStrength(password)
  const strength = getPasswordStrength(score)

  // Check if password is same as current password
  if (currentPassword && password === currentPassword) {
    errors.push('New password must be different from current password')
  }

  // Basic validation errors
  if (!password) {
    errors.push('Password is required')
  } else if (!allRequirementsMet) {
    errors.push('Password does not meet all security requirements')
  }

  return {
    isValid: allRequirementsMet && errors.length === 0,
    strength,
    score,
    requirements,
    errors,
  }
}

/**
 * Check if passwords match
 */
export const checkPasswordsMatch = (password: string, confirmPassword: string): boolean => {
  return password === confirmPassword && password.length > 0
}

/**
 * Get requirement status icon
 */
export const getRequirementIcon = (met: boolean): string => {
  return met ? '✓' : '✗'
}
